/**
 * @-mention encoding for comment bodies (PLAT-10931).
 *
 * A mention is stored inline in the comment body as a token
 * `@[Display Name](personId)`. The person id is the durable reference (the
 * future notifier resolves id → email); the display name is what renders. The
 * `clip_comment.mentions` field denormalizes the ids (derived from the body via
 * {@link parseMentionIds}) so the notifier/search don't have to parse bodies.
 *
 * Pure logic (no JSX) so both the repository (write-time derivation) and the
 * comment components (render) can import it — mirroring `utility/comments.ts`.
 * Person names containing `]` or `)` are not expected (names come from the
 * teammate roster) and are out of scope for the token grammar.
 */

/** Matches one mention token, capturing (1) display name and (2) person id. */
const MENTION_TOKEN = /@\[([^\]]+)\]\(([^)]+)\)/g;

/** Build the inline token inserted into the body when a teammate is picked. */
export function mentionToken(name: string, id: string): string {
  return `@[${name}](${id})`;
}

/** Unique person ids mentioned in a body, in first-appearance order. */
export function parseMentionIds(body: string): string[] {
  const ids: string[] = [];
  for (const match of body.matchAll(MENTION_TOKEN)) {
    const id = match[2];
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** A rendered body segment: plain text, or a mention to style as a chip. */
export type BodySegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; name: string; id: string };

/**
 * Split a body into text + mention segments for rendering. Concatenating the
 * `value`/`@name` of every segment reproduces the visible text.
 */
export function splitBodyForRender(body: string): BodySegment[] {
  const segments: BodySegment[] = [];
  let lastIndex = 0;
  for (const match of body.matchAll(MENTION_TOKEN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ type: 'text', value: body.slice(lastIndex, start) });
    }
    segments.push({ type: 'mention', name: match[1], id: match[2] });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < body.length) {
    segments.push({ type: 'text', value: body.slice(lastIndex) });
  }
  return segments;
}

/** Flatten mention tokens to plain `@Name` (for compact previews / fallbacks). */
export function stripMentionTokens(body: string): string {
  return body.replace(MENTION_TOKEN, (_full, name: string) => `@${name}`);
}

// ---- Editor display form -----------------------------------------------
//
// Editors (composer / reply / edit) hold the friendly display form `@Name`,
// never raw tokens: picking a teammate inserts `@Name`, and the storable
// `@[Name](id)` token is derived at submit time by matching `@Name` runs
// against the known roster (encode). Editing an existing body converts its
// tokens back to display form first (decode).

/** A person a display mention can resolve against. */
export type MentionRef = { id: string; name: string };

/** Visible editor form of a mention: `@Name`. */
export function mentionDisplay(name: string): string {
  return `@${name}`;
}

const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;

/** Alternation regex over `@Name` for the given people (longest name first so
 * overlapping names — "Ada" vs "Ada Lovelace" — resolve to the longer match).
 * `null` when there is no one to match. */
function displayMentionRegex(people: MentionRef[]): RegExp | null {
  if (people.length === 0) return null;
  const names = [...people]
    .sort((a, b) => b.name.length - a.name.length)
    .map(p => p.name.replace(REGEX_SPECIALS, '\\$&'));
  // A display mention starts at a word boundary — the same rule the composer's
  // autocomplete uses to open the popover. Without the lookbehind, an `@` glued
  // to preceding word chars matches too, so an email address in the body
  // ("ada@Ada.com") would be rewritten into a token and contribute a bogus id
  // to `mentions`. `@` is excluded as well so `@@Name` stays literal.
  // It ends at a non-word char (space, punctuation) or the end of the text, so
  // `@Ada L` mid-typing doesn't half-match `@Ada Lovelace`.
  return new RegExp(`(?<![\\w@])@(${names.join('|')})(?=[^\\w]|$)`, 'g');
}

/**
 * Replace `@Name` display mentions with storable `@[Name](id)` tokens for
 * every name that matches someone in `people`. Unknown `@somebody` runs are
 * left as plain text.
 *
 * `people` is matched by display name, and the by-name map is last-wins, so
 * callers append the people the user actually picked (or, when editing, the
 * ones the original body referenced) *after* the roster to override its guess.
 * Two teammates sharing a display name still can't be told apart within one
 * body — resolution has no positions, so every `@Ada Lovelace` in a body
 * resolves to the same id. Atomic mention nodes (a real editor) are the fix if
 * that ever matters.
 */
export function encodeMentionTokens(
  text: string,
  people: MentionRef[],
): string {
  const regex = displayMentionRegex(people);
  if (!regex) return text;
  const byName = new Map(people.map(p => [p.name, p.id]));
  return text.replace(regex, (full, name: string) => {
    const id = byName.get(name);
    return id ? mentionToken(name, id) : full;
  });
}

/**
 * Convert a stored body's tokens to editor display form, returning the people
 * the tokens referenced so a later {@link encodeMentionTokens} can restore
 * them (even if they've since left the roster).
 */
export function decodeMentionTokens(body: string): {
  text: string;
  people: MentionRef[];
} {
  const people: MentionRef[] = [];
  const seen = new Set<string>();
  const text = body.replace(
    MENTION_TOKEN,
    (_full, name: string, id: string) => {
      if (!seen.has(id)) {
        seen.add(id);
        people.push({ id, name });
      }
      return mentionDisplay(name);
    },
  );
  return { text, people };
}

/**
 * Split editor display text into segments for the composer's highlight layer:
 * `@Name` runs matching `people` become mention segments, the rest stays text.
 */
export function splitDisplayForRender(
  text: string,
  people: MentionRef[],
): BodySegment[] {
  const regex = displayMentionRegex(people);
  if (!regex) return text ? [{ type: 'text', value: text }] : [];
  const byName = new Map(people.map(p => [p.name, p.id]));
  const segments: BodySegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(regex)) {
    const start = match.index ?? 0;
    const id = byName.get(match[1]);
    if (!id) continue;
    if (start > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, start) });
    }
    segments.push({ type: 'mention', name: match[1], id });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
}
